import dayjs from 'dayjs';

export const DASHBOARD_TIME_RANGE_PRESETS = [
  { value: 'lastHour', label: 'Last hour' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7days', label: '7 days' },
  { value: 'last30days', label: '30 days' },
  { value: 'last90days', label: '90 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'allTime', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

export const DEFAULT_TIME_RANGE = { period: 'last7days' };

export function formatCustomRangeLabel(startDate, endDate) {
  if (!startDate || !endDate) return 'Custom';
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (start.isSame(end, 'year')) {
    return `${start.format('MMM D')} – ${end.format('MMM D')}`;
  }
  return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
}

export function getTimeRangeLabel(timeRange) {
  if (!timeRange?.period) return '7 days';
  if (timeRange.period === 'custom') {
    return formatCustomRangeLabel(timeRange.startDate, timeRange.endDate);
  }
  const preset = DASHBOARD_TIME_RANGE_PRESETS.find((p) => p.value === timeRange.period);
  return preset?.label || timeRange.period;
}

export function resolveChartGranularity(timeRange) {
  const period = timeRange?.period;
  if (period === 'lastHour') return 'five_minute';
  if (period === 'today' || period === 'yesterday') return 'hour';
  if (period === 'custom' && timeRange.startDate && timeRange.endDate) {
    const spanMs = dayjs(timeRange.endDate).diff(dayjs(timeRange.startDate));
    if (spanMs <= 2 * 24 * 60 * 60 * 1000) return 'hour';
    return 'day';
  }
  if (period === 'allTime') return 'day';
  return 'day';
}

export function timeRangeCacheKey(timeRange, showAll) {
  const period = timeRange?.period || 'last7days';
  if (period === 'custom') {
    return `${period}:${timeRange.startDate || ''}:${timeRange.endDate || ''}:${showAll}`;
  }
  return `${period}:${showAll}`;
}

export function buildStatisticsQueryParams(timeRange, showAll = false) {
  const params = new URLSearchParams();
  const period = timeRange?.period || 'last7days';
  params.append('period', period);
  if (period === 'custom') {
    if (timeRange.startDate) {
      params.append('start_date', timeRange.startDate);
    }
    if (timeRange.endDate) {
      params.append('end_date', timeRange.endDate);
    }
  }
  if (showAll) {
    params.append('show_all', 'true');
  }
  return params;
}

const ROLLING_PERIOD_TOOLTIPS = {
  lastHour: 'Past 60 minutes (rolling)',
  last7days: 'Past 7 days (rolling, 168 hours)',
  last30days: 'Past 30 days (rolling)',
  last90days: 'Past 90 days (rolling)',
};

export function getTimeRangeTooltip(timeRange) {
  const period = timeRange?.period;
  return ROLLING_PERIOD_TOOLTIPS[period] || null;
}

export function formatTimeRangeSubtitle(timeRange, timezoneMode = 'local') {
  if (!timeRange?.period) return null;
  const tzLabel = timezoneMode === 'utc' ? 'UTC' : 'local';
  if (timeRange.period === 'custom' && timeRange.startDate && timeRange.endDate) {
    const label = formatCustomRangeLabel(timeRange.startDate, timeRange.endDate);
    return `${label} (${tzLabel})`;
  }
  const preset = DASHBOARD_TIME_RANGE_PRESETS.find((p) => p.value === timeRange.period);
  if (!preset) return null;
  const tooltip = getTimeRangeTooltip(timeRange);
  if (tooltip) {
    return `${preset.label} · ${tooltip} · ${tzLabel}`;
  }
  return `${preset.label} (${tzLabel})`;
}

/** Map dashboard time range to RAG API period param (custom falls back to last7days + dates). */
export function timeRangeToRagPeriod(timeRange) {
  return timeRange?.period || 'last7days';
}

export function appendTimeRangeToParams(params, timeRange) {
  const period = timeRange?.period || 'last7days';
  params.set('period', period);
  if (period === 'custom') {
    if (timeRange.startDate) params.set('start_date', timeRange.startDate);
    if (timeRange.endDate) params.set('end_date', timeRange.endDate);
  }
  return params;
}
